import { useEffect, useState } from "react";
import { PlusCircle, Trash2, Star, ArrowUp, ArrowDown, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * /admin/testimonials — admin CRUD for the landing testimonials section.
 *
 * Layout: table with in-row Edit / Delete / Set-featured / Move-up / Move-down.
 * Add button opens a modal for creating new rows. Editing opens the same
 * modal pre-filled. After every successful mutation we refetch so the UI
 * stays consistent with the server (in particular the featured-mutex).
 */

function TestimonialModal({ open, initial, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: initial?.name || "",
        role: initial?.role || "",
        quote: initial?.quote || "",
        is_featured: !!initial?.is_featured,
        stars: typeof initial?.stars === "number" ? initial.stars : 5,
        published: !!initial?.published,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!open) return;
        setForm({
            name: initial?.name || "",
            role: initial?.role || "",
            quote: initial?.quote || "",
            is_featured: !!initial?.is_featured,
            stars: typeof initial?.stars === "number" ? initial.stars : 5,
            published: !!initial?.published,
        });
        setErr(null);
    }, [open, initial]);

    if (!open) return null;

    async function save() {
        setBusy(true); setErr(null);
        try {
            if (initial?.id) {
                await api.adminUpdateTestimonial(initial.id, form);
            } else {
                await api.adminCreateTestimonial(form);
            }
            await onSaved?.();
            onClose();
        } catch (e) {
            setErr(e.message || "Save failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(11, 18, 32, 0.55)" }}
            data-testid="testimonial-modal"
        >
            <div className="st-surface bg-white w-full max-w-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="font-serif text-2xl" style={{ color: "var(--st-navy)" }}>
                        {initial?.id ? "Edit testimonial" : "New testimonial"}
                    </h2>
                    <button type="button" className="text-sm" onClick={onClose} data-testid="testimonial-modal-close">
                        <X size={18} />
                    </button>
                </div>
                <label className="block">
                    <span className="text-xs tracking-caps font-semibold" style={{ color: "var(--st-silver-500)" }}>Name</span>
                    <input
                        type="text"
                        className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                        style={{ borderColor: "var(--st-line)" }}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="testimonial-input-name"
                    />
                </label>
                <label className="block">
                    <span className="text-xs tracking-caps font-semibold" style={{ color: "var(--st-silver-500)" }}>Role / Context</span>
                    <input
                        type="text"
                        className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                        style={{ borderColor: "var(--st-line)" }}
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        data-testid="testimonial-input-role"
                    />
                </label>
                <label className="block">
                    <span className="text-xs tracking-caps font-semibold" style={{ color: "var(--st-silver-500)" }}>Quote</span>
                    <textarea
                        rows={5}
                        className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                        style={{ borderColor: "var(--st-line)" }}
                        value={form.quote}
                        onChange={(e) => setForm({ ...form, quote: e.target.value })}
                        data-testid="testimonial-input-quote"
                    />
                    <span className="text-[11px]" style={{ color: "var(--st-silver-500)" }}>
                        Enter the student's testimonial quote verbatim. Do not modify or embellish quotes.
                    </span>
                </label>
                <label className="block">
                    <span className="text-xs tracking-caps font-semibold" style={{ color: "var(--st-silver-500)" }}>Stars</span>
                    <select
                        className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
                        style={{ borderColor: "var(--st-line)" }}
                        value={form.stars}
                        onChange={(e) => setForm({ ...form, stars: parseInt(e.target.value, 10) })}
                        data-testid="testimonial-input-stars"
                    >
                        {[5, 4, 3, 2, 1, 0].map((n) => (
                            <option key={n} value={n}>{n} star{n === 1 ? "" : "s"}</option>
                        ))}
                    </select>
                    <span className="text-[11px]" style={{ color: "var(--st-silver-500)" }}>
                        Default is 5 stars. Set lower only when the testimonial content itself references a rating.
                    </span>
                </label>
                <label className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={form.is_featured}
                        onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                        data-testid="testimonial-input-featured"
                    />
                    <span className="text-sm" style={{ color: "var(--st-ink)" }}>
                        Set as featured (auto-clears the previously featured row)
                    </span>
                </label>
                <div
                    className="text-xs px-3 py-2 rounded"
                    style={{ background: "#FEF6E7", color: "#7A4A00", border: "1px solid #FCE3B0" }}
                    data-testid="testimonial-publish-warning"
                >
                    Only publish testimonials you have vetted and confirmed accurate. Published testimonials appear publicly on the landing page immediately.
                </div>
                <label className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={form.published}
                        onChange={(e) => setForm({ ...form, published: e.target.checked })}
                        data-testid="testimonial-input-published"
                    />
                    <span className="text-sm" style={{ color: "var(--st-ink)" }}>
                        Publish immediately
                    </span>
                </label>
                {err && <div className="text-sm" style={{ color: "#B42318" }} data-testid="testimonial-modal-error">{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="btn-outline-navy text-sm" onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="button" className="btn-royal text-sm" onClick={save} disabled={busy} data-testid="testimonial-modal-save">
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function truncate(s, n) {
    if (!s) return "";
    return s.length > n ? s.slice(0, n).trimEnd() + "…" : s;
}

export default function AdminTestimonials() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    async function refetch() {
        setLoading(true); setError(null);
        try {
            const data = await api.adminListTestimonials();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e.message || "Failed to load testimonials");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { refetch(); }, []);

    async function handleDelete(t) {
        const preview = truncate(t.quote, 40);
        if (!window.confirm(`Delete this testimonial?\n\n"${preview}"`)) return;
        try {
            await api.adminDeleteTestimonial(t.id);
            await refetch();
        } catch (e) {
            alert(e.message || "Delete failed");
        }
    }

    async function handleSetFeatured(t) {
        // Optimistic: locally flip flags, then call PATCH.
        const prev = rows;
        setRows(rows.map((r) => ({ ...r, is_featured: r.id === t.id })));
        try {
            await api.adminUpdateTestimonial(t.id, { is_featured: true });
            await refetch();
        } catch (e) {
            setRows(prev);
            alert(e.message || "Set-featured failed");
        }
    }

    async function moveRow(t, direction) {
        // Only non-featured rows are reorderable. Featured is always visually first.
        const grid = rows.filter((r) => !r.is_featured);
        const idx = grid.findIndex((r) => r.id === t.id);
        if (idx < 0) return;
        const swapIdx = direction === "up" ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= grid.length) return;
        const newOrder = [...grid];
        [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
        try {
            await api.adminReorderTestimonials(newOrder.map((r) => r.id));
            await refetch();
        } catch (e) {
            alert(e.message || "Reorder failed");
        }
    }

    async function handleTogglePublished(t) {
        // Optimistic flip; revert on failure.
        const prev = rows;
        setRows(rows.map((r) => (r.id === t.id ? { ...r, published: !r.published } : r)));
        try {
            await api.adminUpdateTestimonial(t.id, { published: !t.published });
            await refetch();
        } catch (e) {
            setRows(prev);
            alert(e.message || "Publish toggle failed");
        }
    }

    return (
        <div className="p-6 md:p-10 max-w-6xl" data-testid="admin-testimonials-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-serif text-3xl" style={{ color: "var(--st-navy)" }}>Testimonials</h1>
                    <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        Public landing testimonials. Exactly one row can be featured. Featured rows always appear first regardless of order. Only Published rows are visible on the public site.
                    </p>
                </div>
                <button
                    type="button"
                    className="btn-royal text-sm inline-flex items-center gap-2"
                    onClick={() => { setEditing(null); setModalOpen(true); }}
                    data-testid="admin-testimonial-add-btn"
                >
                    <PlusCircle size={16} /> Add testimonial
                </button>
            </div>

            {error && (
                <div className="mt-4 px-3 py-2 rounded text-sm" style={{ background: "#FEF1F2", color: "#B42318", border: "1px solid #FECDCA" }}>
                    {error}
                </div>
            )}

            <div className="st-surface mt-8 overflow-hidden">
                <table className="w-full text-sm" data-testid="admin-testimonials-table">
                    <thead>
                        <tr style={{ background: "var(--st-silver-50)", color: "var(--st-ink-muted)" }}>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Featured</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Status</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Name</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Role</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Quote</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Stars</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Order</th>
                            <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={8} className="px-4 py-6 text-center" style={{ color: "var(--st-ink-muted)" }}>Loading…</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={8} className="px-4 py-6 text-center" style={{ color: "var(--st-ink-muted)" }}>No testimonials yet.</td></tr>
                        )}
                        {rows.map((t) => (
                            <tr key={t.id} className="border-t" style={{ borderColor: "var(--st-line)" }} data-testid={`admin-testimonial-row-${t.id}`}>
                                <td className="px-4 py-3 align-top">
                                    {t.is_featured ? (
                                        <span
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-caps font-semibold"
                                            style={{ background: "var(--st-royal)", color: "var(--st-white)" }}
                                            data-testid={`admin-testimonial-featured-badge-${t.id}`}
                                        >
                                            <Star size={10} strokeWidth={0} fill="currentColor" /> FEATURED
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            className="text-[11px] tracking-caps font-semibold"
                                            style={{ color: "var(--st-royal)" }}
                                            onClick={() => handleSetFeatured(t)}
                                            data-testid={`admin-testimonial-set-featured-${t.id}`}
                                        >
                                            Set featured
                                        </button>
                                    )}
                                </td>
                                <td className="px-4 py-3 align-top">
                                    {t.published ? (
                                        <span
                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] tracking-caps font-semibold"
                                            style={{ background: "#ECFDF3", color: "#067647", border: "1px solid #ABEFC6" }}
                                            data-testid={`admin-testimonial-status-published-${t.id}`}
                                        >
                                            PUBLISHED
                                        </span>
                                    ) : (
                                        <span
                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] tracking-caps font-semibold"
                                            style={{ background: "var(--st-silver-100)", color: "var(--st-ink-muted)" }}
                                            data-testid={`admin-testimonial-status-draft-${t.id}`}
                                        >
                                            DRAFT
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 align-top font-serif" style={{ color: "var(--st-navy)" }}>{t.name}</td>
                                <td className="px-4 py-3 align-top" style={{ color: "var(--st-ink-muted)" }}>{t.role}</td>
                                <td className="px-4 py-3 align-top" style={{ color: "var(--st-ink)" }}>{truncate(t.quote, 80)}</td>
                                <td className="px-4 py-3 align-top" style={{ color: "var(--st-ink-muted)" }}>{t.stars ?? 5}</td>
                                <td className="px-4 py-3 align-top" style={{ color: "var(--st-ink-muted)" }}>{t.order}</td>
                                <td className="px-4 py-3 align-top">
                                    <div className="flex items-center justify-end gap-2">
                                        {!t.is_featured && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="text-xs px-2 py-1 border rounded"
                                                    style={{ borderColor: "var(--st-line)", color: "var(--st-ink-muted)" }}
                                                    onClick={() => moveRow(t, "up")}
                                                    data-testid={`admin-testimonial-move-up-${t.id}`}
                                                    aria-label="Move up"
                                                >
                                                    <ArrowUp size={12} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-xs px-2 py-1 border rounded"
                                                    style={{ borderColor: "var(--st-line)", color: "var(--st-ink-muted)" }}
                                                    onClick={() => moveRow(t, "down")}
                                                    data-testid={`admin-testimonial-move-down-${t.id}`}
                                                    aria-label="Move down"
                                                >
                                                    <ArrowDown size={12} />
                                                </button>
                                            </>
                                        )}
                                        <button
                                            type="button"
                                            className="text-xs px-2 py-1 border rounded"
                                            style={{ borderColor: "var(--st-line)", color: "var(--st-ink)" }}
                                            onClick={() => handleTogglePublished(t)}
                                            data-testid={`admin-testimonial-toggle-published-${t.id}`}
                                        >
                                            {t.published ? "Unpublish" : "Publish"}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-outline-navy text-xs"
                                            onClick={() => { setEditing(t); setModalOpen(true); }}
                                            data-testid={`admin-testimonial-edit-${t.id}`}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            className="text-xs px-2 py-1 border rounded"
                                            style={{ borderColor: "#FECDCA", color: "#B42318" }}
                                            onClick={() => handleDelete(t)}
                                            data-testid={`admin-testimonial-delete-${t.id}`}
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <TestimonialModal
                open={modalOpen}
                initial={editing}
                onClose={() => setModalOpen(false)}
                onSaved={refetch}
            />
        </div>
    );
}
